import { useMemo } from "react";

import Papa from "papaparse";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";

export interface ReportDateRange {
  /** ISO yyyy-mm-dd. */
  from: string;
  /** ISO yyyy-mm-dd. */
  to: string;
}

export interface ReportLayoutProps {
  /** Main page header (e.g. "Vehicle Report"). */
  title: string;
  /** Sub-header explaining the report. */
  description: string;
  /** Optional decorative icon shown next to the title. */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** When provided, a start/end date filter is shown. */
  dateRange?: ReportDateRange;
  onDateRangeChange?: (next: ReportDateRange) => void;
  /** Extra controls rendered to the right of the date filter (e.g. select filters). */
  controls?: React.ReactNode;
  /** Rows used when the user clicks "Download CSV". */
  csvRows?: Record<string, unknown>[];
  /** Base filename for the downloaded CSV (date suffix is appended). */
  csvFilename?: string;
  /** Headline stats row (typically a grid of `HeadlineStat` tiles). */
  headlineStats?: React.ReactNode;
  /** The report body — tables, sections, etc. */
  children: React.ReactNode;
}

/**
 * Shared frame for every Report page so they all look the same at a glance.
 * Provides a title block, a date range filter, extra filter slots, and a
 * CSV download button wired through `papaparse` (same approach as the old
 * `ViewReport` component).
 */
export default function ReportLayout({
  title,
  description,
  icon: Icon,
  dateRange,
  onDateRangeChange,
  controls,
  csvRows,
  csvFilename = "report",
  headlineStats,
  children,
}: ReportLayoutProps) {
  const canDownload = Array.isArray(csvRows) && csvRows.length > 0;

  const datedFilename = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return `${csvFilename}-${today}.csv`;
  }, [csvFilename]);

  const handleCsvDownload = () => {
    if (!canDownload) return;
    const csv = Papa.unparse(csvRows!);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = datedFilename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFromChange = (next: string) => {
    if (!dateRange || !onDateRangeChange) return;
    // Clamp: `from` can't exceed `to`.
    const clamped = next > dateRange.to ? dateRange.to : next;
    onDateRangeChange({ ...dateRange, from: clamped });
  };
  const handleToChange = (next: string) => {
    if (!dateRange || !onDateRangeChange) return;
    const clamped = next < dateRange.from ? dateRange.from : next;
    onDateRangeChange({ ...dateRange, to: clamped });
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Header — title + date/filter controls + CSV button. Everything a
          report needs to "run", on one row on desktop. */}
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex items-start gap-3">
          {Icon && (
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <Icon className="h-5 w-5" aria-hidden />
            </span>
          )}
          <div>
            <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
            <p className="mt-0.5 max-w-2xl text-sm text-slate-500">
              {description}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {dateRange && (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
              <CalendarDaysIcon
                className="mb-1.5 h-4 w-4 flex-none text-slate-400"
                aria-hidden
              />
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  From
                </label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => handleFromChange(e.target.value)}
                  className="mt-0.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  To
                </label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => handleToChange(e.target.value)}
                  className="mt-0.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          {controls}

          <button
            type="button"
            onClick={handleCsvDownload}
            disabled={!canDownload}
            className={classNames(
              "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2",
              canDownload
                ? "border-transparent bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500"
                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
            )}
            title={
              canDownload
                ? `Export ${csvRows!.length} rows to CSV`
                : "No rows to export"
            }
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
            Download CSV
          </button>
        </div>
      </div>

      {headlineStats}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared Filter primitives used by most reports.
// ---------------------------------------------------------------------------

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  /** Width in rem. Defaults to a comfortable 11rem. */
  width?: string;
}

/**
 * Tiny wrapper over a native `<select>` sized and styled to sit neatly on
 * the report header alongside the date range.
 */
export function ReportFilterSelect({
  label,
  value,
  onChange,
  options,
  width,
}: FilterSelectProps) {
  return (
    <div
      className="flex flex-col rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2"
      style={width ? { width } : undefined}
    >
      <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
